import { inject, Injectable } from '@angular/core';
import { WoundtypeOutput } from '@modules/forms/form-modules/woundtrack/models/woundtype-output';
import { EventTopic } from '@modules/studio/event-bus/event-topic.enum';
import { UserTool } from '@modules/studio/models/user-tool.enum';
import { StudioEventService } from '@modules/studio/studio/studio-event.service';
import { WoundtrackClassProportions, WoundtypeResult } from '@pixacare/domain';
import { isNullOrEmpty } from '@pixacare/shared';
import { BehaviorSubject, map } from 'rxjs';
import { WoundtrackOutputService } from './woundtrack-output.service';

// Without an analysis, the values are `null` rather than left out, so completing retracts a previous run's.
const NO_WOUNDTYPE_RESULT: WoundtypeResult = { granulation: null, fibrin: null, necrosis: null };

@Injectable()
export class WoundtypeService {
  private readonly eventService = inject(StudioEventService);
  private readonly outputService = inject(WoundtrackOutputService);

  private readonly woundtypeOutput = new BehaviorSubject<WoundtypeOutput>(null);
  private readonly woundtypeOutput$ = this.woundtypeOutput.pipe(
    map((output) => output ?? { image: null, result: null, items: [] }),
  );

  readonly woundtypeResult$ = this.woundtypeOutput$.pipe(map(({ result }) => result ?? NO_WOUNDTYPE_RESULT));
  readonly woundtypeResultItems$ = this.woundtypeOutput$.pipe(map(({ items }) => items));
  readonly isWoundtypeLoading$ = this.woundtypeOutput$.pipe(map(({ result }) => isNullOrEmpty(result)));

  drawWoundtype(image: string, proportions: WoundtrackClassProportions): void {
    const result = this.outputService.getWoundtypeResults(proportions);
    const resultItems = this.outputService.getWoundtypeResultItems(result);
    this.drawSegmentation(image);
    this.woundtypeOutput.next({
      image,
      result,
      items: resultItems,
    });
  }

  resetWoundtype(): void {
    this.woundtypeOutput.next(null);
  }

  downloadPicture(): void {
    this.eventService.publish(EventTopic.EXECUTE_TOOL, { tool: UserTool.DOWNLOAD });
  }

  private drawSegmentation(image: string): void {
    this.eventService.publish(EventTopic.IMPORT_IMAGE_OVERLAY, { base64: image });
  }
}
